// One invoice as a page: the paper on the left, and beside it what's owed,
// how to send it (link, email, WhatsApp, PDF), payments and reminders.
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { markSent, publicLinkUrl, recordPayment, removePayment, takeNumber, withPublicLink } from '@/app/actions';
import { useAppData } from '@/app/AppData';
import { usePageTitle } from '@/app/usePageTitle';
import { daysBetween, formatDate, nowStamp, today } from '@/core/dates';
import { newId } from '@/core/ids';
import { currencyOfInvoice, daysLate, duplicateInvoice, type Invoice, isDraft, PAYMENT_METHODS, type PaymentMethod, statusOn, totalsOf } from '@/core/invoice';
import { Money } from '@/core/money';
import { invoiceReminderMessage, invoiceReminderSubject, invoiceShareMessage, invoiceShareSubject } from '@/core/shareTexts';
import { track } from '@/data/analytics';
import { deleteInvoice, saveInvoice } from '@/data/store';
import { ScaledPaper } from '@/invoice/InvoicePaper';
import { PrintableInvoice, printInvoice } from '@/invoice/print';
import { clientNameLabel, paymentMethodLabel } from '@/l10n/labels';
import { t } from '@/l10n/strings';
import { NotFoundPage } from '@/pages/System';
import { Button, PageLoading, SelectField, StatusChip, TextField } from '@/ui/controls';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Icon } from '@/ui/Icon';
import { Menu } from '@/ui/Menu';
import { useToast } from '@/ui/Toasts';
import { copyText, emailDraftHref, whatsappHref } from '@/utils/contactLinks';

export function InvoiceDetailPage() {
  const { id } = useParams();
  const { invoices, business } = useAppData();
  const invoice = invoices?.find((i) => i.id === id);
  usePageTitle(invoice ? `${t('webTitleInvoice')} ${invoice.number}` : t('webTitleInvoice'));
  if (!invoices || !business) return <PageLoading />;
  if (!invoice) return <NotFoundPage />;
  return <Detail invoice={invoice} />;
}

function Detail({ invoice }: { invoice: Invoice }) {
  const { business, isPro, user, config } = useAppData();
  const navigate = useNavigate();
  const toast = useToast();
  const uid = user!.uid;
  const [paying, setPaying] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const day = today();
  const status = statusOn(invoice, day);
  const totals = totalsOf(invoice);
  const late = daysLate(invoice, day);
  const owed = !isDraft(invoice) && totals.balance.isPositive;

  const share = async (channel: 'link' | 'email' | 'whatsapp') => {
    let linked = invoice;
    let link: string | undefined;
    if (config.flags.invoiceLinks) {
      linked = withPublicLink(uid, invoice);
      link = publicLinkUrl(linked.publicLinkId!);
    }
    const message = invoiceShareMessage(linked, business!, link);
    if (channel === 'link') {
      if (!link) return;
      const copied = await copyText(link);
      if (!copied) {
        toast(t('previewYourBrowserBlockedCopyingSelect'), { error: true });
        return;
      }
      toast(isDraft(linked) ? t('previewLinkCopiedInvoiceMarkedAs') : t('previewLinkCopied'));
    } else if (channel === 'email') {
      window.location.href = emailDraftHref(invoice.client.email, invoiceShareSubject(linked, business!), message);
    } else {
      window.open(whatsappHref(invoice.client.phone, message), '_blank', 'noopener');
    }
    markSent(uid, linked, channel, channel === 'link' ? 'link' : 'text');
  };

  const savePdf = () => {
    track('invoice_saved', { format: 'pdf', from: 'detail' });
    printInvoice(invoice);
  };

  const duplicate = async () => {
    try {
      const number = await takeNumber(uid, invoice.businessId);
      const copy = duplicateInvoice(invoice, { id: newId(), number, today: day, now: nowStamp(), ids: newId });
      saveInvoice(uid, copy);
      track('invoice_duplicated');
      navigate(`/invoices/${copy.id}/edit`);
    } catch {
      toast(t('previewCouldntSaveTheInvoiceTry'), { error: true });
    }
  };

  const remove = () => {
    deleteInvoice(uid, invoice.id);
    track('invoice_deleted', { was_draft: isDraft(invoice), from: 'detail' });
    toast(t('detailInvoiceDeleted'), {
      action: {
        label: t('commonUndo'),
        run: () => {
          saveInvoice(uid, invoice);
          track('invoice_restored');
        },
      },
    });
    navigate('/invoices', { replace: true });
  };

  const dueLine = isDraft(invoice)
    ? t('detailIssued', { issueDate: formatDate(invoice.issueDate) })
    : status === 'paid'
      ? t('detailPaidInFull')
      : late > 0
        ? t('detailDueLate', { date: formatDate(invoice.dueDate), days: late })
        : t('detailDueIn', { date: formatDate(invoice.dueDate), days: Math.max(0, daysBetween(day, invoice.dueDate)) });

  return (
    <div className="page page--wide">
      <header className="page-head">
        <div className="page-head__title">
          <Link to="/invoices" className="back-link">
            <Icon name="back" size={16} />
            {t('navInvoices')}
          </Link>
          <div className="row-wrap">
            <h1 className="display">{invoice.number}</h1>
            <StatusChip status={status} />
          </div>
          <p className="body muted">
            {clientNameLabel(invoice.client)} · {t('detailIssued', { issueDate: formatDate(invoice.issueDate) })}
          </p>
        </div>
        <div className="page-head__actions">
          <Link to={`/invoices/${invoice.id}/edit`} className="btn btn--secondary">
            <Icon name="pen" size={18} />
            {t('commonEdit')}
          </Link>
          <Button icon="download" onClick={savePdf}>
            {t('detailSavePdf')}
          </Button>
          <Menu
            trigger={(props) => (
              <button type="button" className="iconbtn iconbtn--outlined" aria-label={t('detailMore')} {...props}>
                <Icon name="more" size={18} />
              </button>
            )}
          >
            {(close) => (
              <>
                <button
                  role="menuitem"
                  type="button"
                  className="menu__item"
                  onClick={() => {
                    close();
                    void duplicate();
                  }}
                >
                  <Icon name="copy" size={18} />
                  {t('detailDuplicate')}
                </button>
                <div className="menu__sep" />
                <button
                  role="menuitem"
                  type="button"
                  className="menu__item menu__item--danger"
                  onClick={() => {
                    close();
                    setDeleting(true);
                  }}
                >
                  <Icon name="trash" size={18} />
                  {t('detailDeleteInvoice')}
                </button>
              </>
            )}
          </Menu>
        </div>
      </header>

      <div className="detail">
        <div className="detail__paper">
          <ScaledPaper invoice={invoice} business={business!} isPro={isPro} maxWidth={820} />
        </div>
        <aside className="detail__side stack-4">
          <section className="card stack-3" aria-labelledby="owed">
            <span className="caption" id="owed">
              {owed ? t('detailStillOwed') : t('detailTotal')}
            </span>
            <b className="amount-hero" style={{ fontSize: 44, lineHeight: '48px' }}>
              {(owed ? totals.balance : totals.total).format()}
            </b>
            <span className={`label${late > 0 && owed ? ' danger-text' : ' ink2'}`}>{dueLine}</span>
            {totals.paid.isPositive && (
              <>
                <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={totals.total.minor} aria-valuenow={Math.min(totals.paid.minor, totals.total.minor)} aria-label={t('detailPaidOfTotal', { paid: totals.paid.format(), total: totals.total.format() })}>
                  <i style={{ width: `${Math.min(100, (totals.paid.minor / Math.max(1, totals.total.minor)) * 100)}%` }} />
                </div>
                <span className="caption">{t('detailPaidOfTotal', { paid: totals.paid.format(), total: totals.total.format() })}</span>
              </>
            )}
            {owed ? (
              <Button variant="action" size="lg" icon="money" block onClick={() => setPaying(true)}>
                {t('detailRecordPayment')}
              </Button>
            ) : (
              isDraft(invoice) && (
                <p className="caption">{t('webSendToMarkSent')}</p>
              )
            )}
          </section>

          <section className="card stack-3" aria-labelledby="send">
            <h2 className="headline" id="send">
              {isDraft(invoice) ? t('previewSendInvoice') : t('previewSendAgain')}
            </h2>
            <div className="share-grid">
              {config.flags.invoiceLinks && (
                <button type="button" className="share" onClick={() => share('link')}>
                  <span>
                    <Icon name="link" />
                  </span>
                  {t('previewCopyLink')}
                </button>
              )}
              <button type="button" className="share share--wa" onClick={() => share('whatsapp')}>
                <span>
                  <Icon name="wa" />
                </span>
                {t('clientDetailWhatsApp')}
              </button>
              <button type="button" className="share" onClick={() => share('email')}>
                <span>
                  <Icon name="mail" />
                </span>
                {t('previewEmail')}
              </button>
              <button type="button" className="share" onClick={savePdf}>
                <span>
                  <Icon name="download" />
                </span>
                {t('previewSendAsPdf')}
              </button>
            </div>
            {config.flags.invoiceLinks && <p className="caption">{t('previewAnyoneWithThisLinkCan')}</p>}
            {invoice.publicLinkId && (
              <a className="label row" style={{ gap: 6 }} href={publicLinkUrl(invoice.publicLinkId)} target="_blank" rel="noreferrer">
                <Icon name="external" size={16} />
                {t('webOpenClientPage')}
              </a>
            )}
            {owed && (
              <Button icon="ring" onClick={() => setReminding(true)}>
                {t('detailRemind')}
              </Button>
            )}
          </section>

          <section className="card stack-2" aria-labelledby="activity">
            <h2 className="headline" id="activity">
              {t('detailActivity')}
            </h2>
            <ol className="timeline">
              {[...invoice.payments]
                .sort((a, b) => (a.date < b.date ? 1 : -1))
                .map((p) => (
                  <li key={p.id}>
                    <span className="timeline__dot timeline__dot--paid" />
                    <div className="grow stack-1">
                      <b className="body-strong">{t('detailPaymentReceived', { amount: new Money(p.amountMinor, currencyOfInvoice(invoice)).format(), method: paymentMethodLabel(p.method) })}</b>
                      <span className="caption">
                        {formatDate(p.date)}
                        {p.note && ` · ${p.note}`}
                      </span>
                    </div>
                    <button type="button" className="iconbtn iconbtn--sm" aria-label={t('detailRemovePayment')} onClick={() => setRemoving(p.id)}>
                      <Icon name="x" size={16} />
                    </button>
                  </li>
                ))}
              {invoice.sentAt && (
                <li>
                  <span className="timeline__dot" />
                  <div className="stack-1">
                    <b className="body-strong">{t('detailSentToTheClient')}</b>
                    <span className="caption">{formatDate(invoice.sentAt)}</span>
                  </div>
                </li>
              )}
              <li>
                <span className="timeline__dot" />
                <div className="stack-1">
                  <b className="body-strong">{t('detailInvoiceCreated')}</b>
                  <span className="caption">{t('detailInvoiceCreatedDetail', { date: formatDate(invoice.createdAt), count: invoice.items.length })}</span>
                </div>
              </li>
            </ol>
          </section>
        </aside>
      </div>

      <PrintableInvoice invoice={invoice} business={business!} isPro={isPro} />
      {paying && <PaymentDialog invoice={invoice} onClose={() => setPaying(false)} onSave={(p) => recordPayment(uid, invoice, p)} />}
      {reminding && <ReminderDialog invoice={invoice} onClose={() => setReminding(false)} />}
      {removing && (
        <ConfirmDialog
          open
          danger
          title={t('detailRemoveThisPayment')}
          message={(() => {
            const p = invoice.payments.find((x) => x.id === removing)!;
            return t('detailRemovePaymentMessage', { amount: new Money(p.amountMinor, currencyOfInvoice(invoice)).format(), date: formatDate(p.date) });
          })()}
          confirmLabel={t('detailRemovePayment')}
          onConfirm={() => removePayment(uid, invoice, removing)}
          onClose={() => setRemoving(null)}
        />
      )}
      <ConfirmDialog open={deleting} danger title={t('detailDeleteThisInvoice')} message={t('detailDeleteMessage', { number: invoice.number })} confirmLabel={t('detailDeleteInvoice')} onConfirm={remove} onClose={() => setDeleting(false)} />
    </div>
  );
}

function PaymentDialog({ invoice, onClose, onSave }: { invoice: Invoice; onClose: () => void; onSave: (payment: { id: string; amountMinor: number; date: string; method: PaymentMethod; note: string }) => void }) {
  const currency = currencyOfInvoice(invoice);
  const balance = totalsOf(invoice).balance;
  const [amount, setAmount] = useState(balance.format({ withSymbol: false }));
  const [date, setDate] = useState(today());
  const [method, setMethod] = useState<PaymentMethod>('bankTransfer');
  const [note, setNote] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const parsed = Money.tryParse(amount, currency);
  const error = !parsed || !parsed.isPositive ? t('paymentEnterTheAmountYouReceived') : null;
  const after = parsed ? balance.minus(parsed) : null;
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    setShowErrors(true);
    if (error || !parsed) return;
    onSave({ id: newId(), amountMinor: parsed.minor, date, method, note: note.trim() });
    onClose();
  };
  const half = new Money(Math.round(balance.minor / 2), currency);
  return (
    <Dialog
      open
      onClose={onClose}
      onSubmit={submit}
      title={t('paymentRecordPayment')}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('commonCancel')}
          </Button>
          <Button type="submit" variant="action">
            {t('paymentSavePayment')}
          </Button>
        </>
      }
    >
      <p className="caption">{t('paymentStillOwed', { number: invoice.number, owed: balance.format() })}</p>
      <TextField
        label={`${t('paymentAmountReceived')} (${currency.symbol})`}
        inputMode="decimal"
        value={amount}
        autoFocus
        onChange={(e) => setAmount(e.target.value)}
        error={showErrors ? error : null}
        help={after ? (after.isPositive ? t('paymentWillStillBeOwed', { amount: after.format() }) : after.isZero ? t('paymentTheInvoiceWillBeMarked') : t('paymentThatIsMoreThanWhat', { owed: after.negate().format() })) : undefined}
      />
      <div className="row-wrap">
        <button type="button" className="tab" onClick={() => setAmount(balance.format({ withSymbol: false }))}>
          {t('paymentFull', { owed: balance.format() })}
        </button>
        <button type="button" className="tab" onClick={() => setAmount(half.format({ withSymbol: false }))}>
          {t('paymentHalf')}
        </button>
      </div>
      <div className="grid-2">
        <TextField label={t('paymentDate')} type="date" value={date} max={today()} onChange={(e) => e.target.value && setDate(e.target.value)} />
        <SelectField label={t('paymentHowWereYouPaid')} value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
          {PAYMENT_METHODS.map((m) => (
            <option key={m} value={m}>
              {paymentMethodLabel(m)}
            </option>
          ))}
        </SelectField>
      </div>
      <TextField label={t('paymentNote')} value={note} placeholder={t('paymentOptionalEGReferenceNumber')} onChange={(e) => setNote(e.target.value)} />
    </Dialog>
  );
}

function ReminderDialog({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const { business, config } = useAppData();
  const toast = useToast();
  const link = config.flags.invoiceLinks && invoice.publicLinkId ? publicLinkUrl(invoice.publicLinkId) : null;
  const [message, setMessage] = useState(() => invoiceReminderMessage(invoice, business!, today()) + (link ? `\n\n${t('shareViewOnline', { link })}` : ''));
  const sent = () => {
    track('reminder_sent', { overdue: daysLate(invoice, today()) > 0 });
    onClose();
  };
  return (
    <Dialog open onClose={onClose} title={t('detailRemind')} wide>
      <label className="field">
        <span className="field__label">{t('webReminderMessage')}</span>
        <textarea className="input" rows={9} value={message} onChange={(e) => setMessage(e.target.value)} />
      </label>
      <div className="dialog__actions">
        <Button
          icon="copy"
          onClick={async () => {
            if (await copyText(message)) {
              toast(t('webMessageCopied'));
              sent();
            }
          }}
        >
          {t('webCopyMessage')}
        </Button>
        <a className="btn btn--secondary" href={emailDraftHref(invoice.client.email, invoiceReminderSubject(invoice, business!), message)} onClick={sent}>
          <Icon name="mail" size={18} />
          {t('previewEmail')}
        </a>
        <a className="btn btn--whatsapp" href={whatsappHref(invoice.client.phone, message)} target="_blank" rel="noreferrer" onClick={sent}>
          <Icon name="wa" size={18} />
          {t('clientDetailWhatsApp')}
        </a>
      </div>
    </Dialog>
  );
}
